import { Injectable, Inject } from '@angular/core'
import { APIService } from 'api/module'
import { Logger } from 'common/log'

import { TranslateService } from '@ngx-translate/core'
import { BootstrapData, MediaFile, MediaFileMini, MediaFileBundle, MediaFileBundleMiniReference } from 'elements-sdk'

import { BOOTSTRAP_DATA } from 'api/providers'
import { IdentityService } from 'main/services/identity.service'
import { ElementsClientService } from 'common/util.elementsClient/services/elementsClient.service'
import { NotifyService } from 'common/util/services/notify.service'

@Injectable({ providedIn: 'root' })
export class ShowInExplorerService {
  logger: Logger = new Logger(__filename)
  private clientOS: string

  constructor (
    private api: APIService,
    private identity: IdentityService,
    private elementsClient: ElementsClientService,
    private translate: TranslateService,
    private notify: NotifyService,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
  ) {
    this.clientOS = bootstrapData.clientOs
  }

  getOpenLabel (bundle: MediaFileBundle|null): string {
    const name = bundle?.mainfile.name ?? ''
    const ext = name.substring(name.lastIndexOf('.')).toLowerCase()
    if (ext === '.prproj') {
      return this.translate.instant('Open in Premiere Pro')
    }
    if (ext === '.aep') {
      return this.translate.instant('Open in After Effects')
    }
    if (ext === '.avp') {
      return this.translate.instant('Open in Avid Media Composer')
    }
    return this.translate.instant('Open locally')
  }

  getShowLabel (): string {
    if (this.clientOS === 'mac') {
      return this.translate.instant('Show in Finder')
    }
    if (this.clientOS === 'windows') {
      return this.translate.instant('Show in Explorer')
    }
    return this.translate.instant('Show in file manager')
  }

  async openBundle (bundle: MediaFileBundleMiniReference): Promise<void> {
    this.logger.info('Opening bundle in Explorer:', bundle)
    let file = bundle.mainfile
    if (!file) {
      const files = await this.api.mediaLibrary.getAllMediaFiles({
        bundle: bundle.id,
      })
      file = files[0]
    }
    return this.openFile(file)
  }

  async openFile (file: MediaFileMini): Promise<void> {
    this.logger.info('Opening file in Explorer:', file)
    const params = await this.api.private.locateFile({
      locateEndpointRequest: {
        path: file.fullPath,
      },
    })
    if (!params.workspace) {
      this.notify.error(this.translate.instant('This directory is not accessible through any workspaces that you have access to'))
      return
    }

    this.elementsClient.browseToFile(params.workspaceRelativePath!, params.workspace)
    this.notify.info(this.translate.instant('Opening'))
  }

  async showBundle (bundle: MediaFileBundleMiniReference): Promise<void> {
    this.logger.info('Showing bundle in Explorer:', bundle)
    if (bundle.mainfile) {
      return this.showFile(bundle.mainfile)
    } else {
      const files = await this.api.mediaLibrary.getAllMediaFiles({
        bundle: bundle.id,
      })
      return this.showFile(files[0])
    }
  }

  async showFile (file: MediaFileMini): Promise<void> {
    this.logger.info('Showing file in Explorer:', file)
    const params = await this.api.private.locateFile({
      locateEndpointRequest: {
        path: file.fullPath,
      },
    })

    if (!params.workspace || !params.workspaceRelativePath) {
      this.notify.error(this.translate.instant('This directory is not accessible through any workspaces that you have access to'))
      return
    }
    this.elementsClient.showFile(params.workspaceRelativePath, params.workspace)
    this.notify.info(this.translate.instant('Opening'))
  }

  async openFTP (file: MediaFile): Promise<void> {
    this.logger.info('Opening FTP for file:', file)
    try {
      const params = await this.api.private.locateFile({
        locateEndpointRequest: {
          path: file.fullPath,
        },
      })
      const user = this.identity.user?.unixUsername
      if (!params.workspace || !params.workspaceRelativePath || !user) {
        this.notify.error(this.translate.instant('This directory is not accessible through any workspaces that you have access to'))
        return
      }

      const workspace = await this.api.storage.getWorkspace({ id: params.workspace })
      const url = `ftp://${user}@${location.hostname}/${workspace.currentShareName}/${params.workspaceRelativePath}`
      this.logger.info('Opening', url)
      this.notify.info(this.translate.instant('Opening'))
      location.href = url
    } catch (error) {
      this.notify.error(this.translate.instant('Error'), error)
    }
  }
}
